// The conductor turns shared server time into sound and light. It schedules
// audio slightly ahead with the Web Audio clock (the classic "lookahead"
// pattern) and queues visual events for the exact moment each note is heard,
// so the ring flash, the globe ripple and the sound line up for every listener.

import {
  MODES,
  NOTE_NAMES,
  STEP_MS,
  STEPS,
  harmonyForBar,
  laneToMidi,
  type Harmony,
  type ModeName,
  type VoiceName,
} from "../shared/music";
import { cellAge, cellAlive, type Cell } from "../shared/protocol";
import type { AudioEngine } from "./audio/engine";
import { localNow } from "./net";

const LOOKAHEAD_MS = 160;
const TICK_MS = 25;

export interface ConductorHooks {
  serverNow(): number;
  epoch(): number;
  cellsAtStep(step: number): Cell[];
  /** Stereo position and facing of a place on the globe as currently seen. */
  spatial(lat: number, lon: number): { pan: number; facing: number };
  percussionIntensity(): number;
  droneLevel(): number;
  onNote(cell: Cell, bar: number): void;
  onStep(step: number, bar: number): void;
  onBar(bar: number, harmony: Harmony): void;
}

interface VisualEvent {
  at: number;
  fn: () => void;
}

export class Conductor {
  private nextStep = -1;
  private timer: number | undefined;
  private visuals: VisualEvent[] = [];
  private harmonyCache = new Map<number, Harmony>();
  /** For constellation playback: freeze the key and mode. */
  harmonyOverride: { root: number; mode: ModeName } | null = null;
  lastHarmony: Harmony | null = null;

  constructor(private readonly engine: AudioEngine, private readonly hooks: ConductorHooks) {}

  start(): void {
    this.stop();
    this.nextStep = -1;
    this.timer = window.setInterval(() => this.tick(), TICK_MS);
    this.tick();
  }

  stop(): void {
    window.clearInterval(this.timer);
    this.timer = undefined;
  }

  harmony(bar: number): Harmony {
    let h = this.harmonyCache.get(bar);
    if (!h) {
      h = harmonyForBar(bar, this.hooks.epoch());
      if (this.harmonyOverride) {
        const mode = this.harmonyOverride.mode;
        h = { ...h, root: this.harmonyOverride.root, rootName: NOTE_NAMES[this.harmonyOverride.root]!, mode, scale: MODES[mode] };
      }
      this.harmonyCache.set(bar, h);
      if (this.harmonyCache.size > 16) {
        const first = this.harmonyCache.keys().next().value;
        if (first !== undefined) this.harmonyCache.delete(first);
      }
    }
    return h;
  }

  resetHarmonyCache(): void {
    this.harmonyCache.clear();
  }

  /** Converts a server timestamp into AudioContext time, compensating output latency. */
  ctxTime(serverMs: number): number {
    const now = this.hooks.serverNow();
    return this.engine.currentTime + (serverMs - now) / 1000 - this.engine.outputLatency;
  }

  /** Local performance-clock time (ms since timeOrigin-relative epoch) at which a server instant is heard. */
  localTime(serverMs: number): number {
    return serverMs - (this.hooks.serverNow() - localNow());
  }

  at(serverMs: number, fn: () => void): void {
    this.visuals.push({ at: this.localTime(serverMs), fn });
  }

  /** Runs due visual events; call from requestAnimationFrame. */
  flushVisuals(): void {
    const now = localNow();
    if (!this.visuals.length) return;
    const due: VisualEvent[] = [];
    const rest: VisualEvent[] = [];
    for (const v of this.visuals) (v.at <= now ? due : rest).push(v);
    this.visuals = rest;
    for (const v of due) v.fn();
  }

  private tick(): void {
    const serverNow = this.hooks.serverNow();
    const epoch = this.hooks.epoch();
    const current = Math.floor((serverNow - epoch) / STEP_MS);
    // First run, or we fell far behind (hidden tab): jump to now.
    if (this.nextStep < 0 || this.nextStep < current - 2 || this.nextStep > current + 8) this.nextStep = current + 1;
    while (epoch + this.nextStep * STEP_MS < serverNow + LOOKAHEAD_MS) {
      this.schedule(this.nextStep);
      this.nextStep++;
    }
  }

  private schedule(n: number): void {
    const epoch = this.hooks.epoch();
    const bar = Math.floor(n / STEPS);
    const step = ((n % STEPS) + STEPS) % STEPS;
    const tServer = epoch + n * STEP_MS;
    const h = this.harmony(bar);
    const audio = this.engine.started;
    const when = audio ? Math.max(this.engine.currentTime, this.ctxTime(tServer)) : 0;

    if (step === 0) {
      if (audio) {
        this.engine.setHarmony(h, when);
        this.engine.setDroneLevel(this.hooks.droneLevel());
      }
      this.at(tServer, () => {
        this.lastHarmony = h;
        this.hooks.onBar(bar, h);
      });
    }
    if (audio) this.engine.percussion(step, when, this.hooks.percussionIntensity());

    const cells = this.hooks.cellsAtStep(step).filter((c) => cellAlive(c, bar));
    for (const c of cells) {
      if (audio) {
        const { pan, facing } = this.hooks.spatial(c.lat, c.lon);
        const age = cellAge(c, bar);
        this.engine.playNote({
          voice: c.voice,
          midi: laneToMidi(c.lane, h, c.voice),
          when,
          vel: 0.62 + (1 - age) * 0.25 + (step % 4 === 0 ? 0.08 : 0),
          pan,
          // Notes from the far side of the planet sound further away.
          age: Math.min(1, age * 0.8 + (facing < 0 ? 0.35 : 0)),
          accent: step % 4 === 0,
        });
      }
      this.at(tServer, () => this.hooks.onNote(c, bar));
    }
    this.at(tServer, () => this.hooks.onStep(step, bar));
  }

  /** Plays a free (unquantised to the loop) note, snapped to the next 32nd for groove. */
  pluck(opts: { lane: number; voice: VoiceName; vel: number; lat: number; lon: number; atServer?: number }): number {
    const epoch = this.hooks.epoch();
    const target = opts.atServer ?? this.hooks.serverNow();
    const half = STEP_MS / 2;
    const snapped = epoch + Math.ceil((target - epoch) / half) * half;
    if (this.engine.started) {
      const bar = Math.floor((snapped - epoch) / (STEP_MS * STEPS));
      const h = this.harmony(bar);
      const { pan, facing } = this.hooks.spatial(opts.lat, opts.lon);
      this.engine.playNote({
        voice: opts.voice,
        midi: laneToMidi(opts.lane, h, opts.voice),
        when: Math.max(this.engine.currentTime, this.ctxTime(snapped)),
        vel: 0.5 + opts.vel * 0.45,
        pan,
        age: facing < 0 ? 0.3 : 0,
        accent: true,
      });
    }
    return snapped;
  }
}
